import React, { createContext, useState, useContext, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  createWidget,
  deleteWidget,
  getWidgetsByDashboardId,
  updateWidget,
} from "../services/apiWidgests";
import { useDashboards } from "./DashboardContext";
import { useAppContext } from "./AppContext";
import getSocket from "../services/sockets/socketClient";
import { toEffectivePx, pxToPercent } from "../utils/layoutUnits";
import { rectsOverlap, resolveLayout } from "../utils/collisionLayout";
const WidgetContext = createContext();
export const useWidgets = () => useContext(WidgetContext);

export const WidgetProvider = ({ children }) => {
  const { auth } = useAuth();
  const { addToast } = useAppContext();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const { dashboard, selectedTab} = useDashboards();

  // Mismo criterio que ya usa DashboardContext.js: sesión vencida (419) o
  // no autorizado (403) manda a /logout en vez de mostrar un error rojo
  // como si fuera un problema del servidor.
  const handleWidgetRequestError = (error) => {
    const status = error?.response?.status;
    if ([419].includes(status)) {
      addToast("Sesión expirada", 1);
      navigate('/logout');
      return;
    }
    if ([403].includes(status)) {
      addToast("No autorizado", 2);
      navigate('/logout');
      return;
    }
    if ([500].includes(status)) {
      addToast("Ocurrio un error con el servidor, intente nuevamente", 3);
      return;
    }
    addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
    console.log(error);
  };
  const [loadingWidgets, setLoadingWidgets] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);

  // Ancho real del contenedor del dashboard, para poder convertir los
  // widgets guardados en % ('layoutUnit' === 'pct') a pixeles efectivos, y
  // recalcularlo en vivo si la ventana cambia de tamaño (responsive).
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    // containerRef.current puede no existir todavia en el primer render de
    // este provider (el <div> que lo setea vive en DashboardSpace, más
    // abajo en el árbol) - este intervalo corto solo detecta ese montaje
    // inicial y queda como red de contención ante cualquier cambio de
    // layout que no dispare 'resize'.
    const interval = setInterval(updateWidth, 500);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearInterval(interval);
    };
  }, []);

  // Convierte una lista de cards (unidades mixtas: px legado o % nuevo) a
  // su equivalente en pixeles efectivos para el containerWidth actual.
  const withPx = (list) => list.map((c) => ({ ...c, ...toEffectivePx(c, containerWidth) }));

  const [cards, setCards] = useState([]);
  // Mientras se arrastra/redimensiona una card, acá se guarda su posición
  // "en vivo" (no confirmada). No se escribe en `cards` ni se persiste
  // hasta soltar el mouse - la card arrastrada sigue al cursor 1:1, y las
  // demás se muestran ya acomodadas a la posición que van a terminar
  // teniendo (preview), gracias a `resolveLayout`.
  const [dragPreview, setDragPreview] = useState(null);
  const refreshWidgetList = async () => {
    try {
      setLoadingWidgets(true);

      if (!dashboard?._id) return;

      deleteAllCard(); // esto es sync

      const response = await getWidgetsByDashboardId(
        dashboard._id,
        auth?.token
      );

      const widgets = response.filter((card) =>
        dashboard.isTabulated ? card.filteredTab === selectedTab : true
      );

      // importante: NO usar addCard uno por uno (usa stale state)
      // mejor reconstruir el arreglo de una vez
      setCards(
        widgets.map((widget) => ({
          id: widget._id,
          title: widget.title,
          type: widget.type,
          dashboardId: widget.dashboardId,
          x: widget.x,
          y: widget.y,
          width: widget.width,
          height: widget.height,
          layoutUnit: widget.layoutUnit || 'px',
          filteredTab: widget.filteredTab,
          headerColor: widget.configuration?.headerColor,
          headerTextColor: widget.configuration?.headerTextColor
        }))
      );
    } catch (e) {
      addToast("Ocurrió un error inesperado", 3);
    } finally {
      setLoadingWidgets(false);
    }
  };


  // OJO: NO depender del objeto `dashboard` completo acá. Cada vez que
  // llega un `dashboard:updated` por socket (cambio de nombre, filtro de
  // fecha, factor, etc.) se hace `setDashboard(nuevoObjeto)` con una
  // referencia nueva - si este efecto dependiera de `dashboard`, se
  // dispararía en CADA cambio de metadata y volvería a pedir/reconstruir
  // TODA la lista de widgets (parpadeo completo del dashboard). Solo debe
  // recargarse la lista cuando cambia el dashboard en sí (otro _id), se
  // activa/desactiva la tabulación, o cambia el tab seleccionado.
  useEffect(() => {
    refreshWidgetList();
  }, [dashboard?._id, dashboard?.isTabulated, selectedTab]);

  // Si otro usuario (u otra pestaña) agrega, mueve, edita o elimina un
  // widget de este mismo dashboard, se refleja en vivo. A propósito NO se
  // hace un refetch completo de la lista (refreshWidgetList) para esto:
  // eso vacía `cards` y lo vuelve a llenar, lo que desmonta y remonta TODAS
  // las cards del dashboard (parpadeo completo con solo mover una). En vez
  // de eso, se parchea únicamente la card afectada.
  const widgetToCard = (widget) => ({
    id: widget._id,
    title: widget.title,
    type: widget.type,
    dashboardId: widget.dashboardId,
    x: widget.x,
    y: widget.y,
    width: widget.width,
    height: widget.height,
    layoutUnit: widget.layoutUnit || 'px',
    filteredTab: widget.filteredTab,
    headerColor: widget.configuration?.headerColor,
    headerTextColor: widget.configuration?.headerTextColor,
  });

  useEffect(() => {
    const dashboardId = dashboard?._id;
    if (!dashboardId) return;

    const socket = getSocket();

    const onWidgetAdded = (payload) => {
      console.log('[WidgetContext] dashboard:widget-added recibido', payload);
      if (payload.dashboardId !== dashboardId) return;
      const widget = payload.widget;
      if (dashboard?.isTabulated && widget.filteredTab !== selectedTab) return;
      setCards((prev) => (prev.some((c) => c.id === widget._id) ? prev : [...prev, widgetToCard(widget)]));
    };

    const onWidgetPatched = (payload) => {
      console.log('[WidgetContext] widget movido/actualizado recibido', payload);
      if (payload.dashboardId !== dashboardId) return;
      const widget = payload.widget;
      setCards((prev) => prev.map((c) => (c.id === widget._id ? { ...c, ...widgetToCard(widget) } : c)));
    };

    const onWidgetRemoved = (payload) => {
      console.log('[WidgetContext] dashboard:widget-removed recibido', payload);
      if (payload.dashboardId !== dashboardId) return;
      setCards((prev) => prev.filter((c) => c.id !== payload.widgetId));
    };

    socket.on("dashboard:widget-added", onWidgetAdded);
    socket.on("dashboard:widget-moved", onWidgetPatched);
    socket.on("dashboard:widget-updated", onWidgetPatched);
    socket.on("dashboard:widget-removed", onWidgetRemoved);

    return () => {
      socket.off("dashboard:widget-added", onWidgetAdded);
      socket.off("dashboard:widget-moved", onWidgetPatched);
      socket.off("dashboard:widget-updated", onWidgetPatched);
      socket.off("dashboard:widget-removed", onWidgetRemoved);
    };
  }, [dashboard?._id, dashboard?.isTabulated, selectedTab]);



  // Card "actual" para un id: si se está arrastrando/redimensionando esa
  // misma card, su posición en vivo (dragPreview); si no, la última
  // posición confirmada (en px efectivos).
  const getCurrentPxCard = (id) => {
    if (dragPreview && dragPreview.id === id) {
      const base = withPx(cards).find((c) => c.id === id);
      return { ...base, ...dragPreview };
    }
    return withPx(cards).find((c) => c.id === id);
  };

  // Drag/resize: SOLO actualizan el preview en vivo. No tocan `cards` ni
  // llaman al backend - la card que estás moviendo sigue al cursor 1:1, y
  // el resto se previsualiza ya acomodado (ver `previewCards` más abajo),
  // pero nada se confirma hasta soltar el mouse (`updateCard`).
  const handleDrag = (id, newX, newY) => {
    const current = getCurrentPxCard(id);
    if (!current) return;
    setDragPreview({
      id,
      x: Math.max(newX, 0),
      y: Math.max(newY, 0),
      width: current.width,
      height: current.height,
    });
  };

  const handleResize = (id, newWidth, newHeight) => {
    const current = getCurrentPxCard(id);
    if (!current) return;
    setDragPreview({
      id,
      x: current.x,
      y: current.y,
      width: newWidth,
      height: newHeight,
    });
  };

  const addCard = async (
    cardId,
    title,
    type,
    dashboardId,
    x,
    y,
    width,
    height,
    filteredTab = ""
  ) => {
    cardId = cardId ?? Date.now();
    title = title ?? `Widget ${Math.random().toString(36).substr(2, 5)}`;
    dashboardId = dashboardId ?? "defaultDashboard";

    setCards((prevCards) => {
      // Evitar cards duplicados: el chequeo se hace ACA, con el estado más
      // reciente dentro del updater, no contra la variable `cards` de
      // afuera (que puede estar stale). Esto importa porque al crear un
      // widget pueden llegar dos caminos casi al mismo tiempo agregando el
      // mismo id: la respuesta del POST (que llama a addCard directo) y el
      // evento de socket "dashboard:widget-added" (el creador también está
      // en la room del dashboard y se lo emite a sí mismo) - sin este
      // chequeo "fresco" terminaba agregándose dos veces.
      if (prevCards.some((card) => card.id === cardId)) {
        return prevCards;
      }
      const pxCards = withPx(prevCards);
      const lastCardPx = pxCards[pxCards.length - 1];
      let newCardX = x ?? (lastCardPx ? lastCardPx.x + 220 : 0);
      let newCardY = y ?? (lastCardPx ? lastCardPx.y : 0);
      let cardWidth = width ?? 200;
      let cardHeight = height ?? 200;

      if (x == null || y == null) {
        let spaceFound = false;
        while (!spaceFound) {
          spaceFound = true;
          for (let card of pxCards) {
            if (
              rectsOverlap(
                {
                  x: newCardX,
                  y: newCardY,
                  width: cardWidth,
                  height: cardHeight,
                },
                card
              )
            ) {
              spaceFound = false;
              newCardX += 220;
              if (newCardX + cardWidth > containerRef.current.offsetWidth) {
                newCardX = 0;
                newCardY += 220;
              }
              break;
            }
          }
        }
      }

      return [
        ...prevCards,
        {
          id: cardId, // Usa el ID proporcionado o el predeterminado
          x: newCardX,
          y: newCardY,
          width: cardWidth,
          height: cardHeight,
          title: title, // Usa el título proporcionado o el predeterminado
          type: type, // Usa el tipo proporcionado o el predeterminado
          dashboardId: dashboardId, // Usa el dashboardId proporcionado o el predeterminado
          filteredTab
        },
      ];
    });
  };

  const assignSpace = () => {
    const pxCards = withPx(cards);
    const lastCard = pxCards[pxCards.length - 1];
    let newCardX = lastCard ? lastCard.x + 220 : 0;
    let newCardY = lastCard ? lastCard.y : 0;

    let spaceFound = false;
    while (!spaceFound) {
      spaceFound = true;
      for (let card of pxCards) {
        if (
          rectsOverlap(
            { x: newCardX, y: newCardY, width: 200, height: 200 },
            card
          )
        ) {
          spaceFound = false;
          newCardX += 220;
          if (newCardX + 200 > containerRef.current.offsetWidth) {
            newCardX = 0;
            newCardY += 220;
          }
          break;
        }
      }
    }
    return {x: newCardX, y:newCardY}
  }
  const createCard = async (type, title, config, dashboardId) => {
    const pxCardsForNew = withPx(cards);
    const lastCard = pxCardsForNew[pxCardsForNew.length - 1];
    let newCardX = lastCard ? lastCard.x + 220 : 0;
    let newCardY = lastCard ? lastCard.y : 0;

    let spaceFound = false;
    while (!spaceFound) {
      spaceFound = true;
      for (let card of pxCardsForNew) {
        if (
          rectsOverlap(
            { x: newCardX, y: newCardY, width: 200, height: 200 },
            card
          )
        ) {
          spaceFound = false;
          newCardX += 220;
          if (newCardX + 200 > containerRef.current.offsetWidth) {
            newCardX = 0;
            newCardY += 220;
          }
          break;
        }
      }
    }

    try {
      console.log("createCard - WidgetProvider");
      const resCreateWidget = await createWidget(
        {
          type: type,
          title: title,
          description: "",
          configuration: config,
          dashboardId: dashboardId,
          x: newCardX,
          y: newCardY,
          width: 200,
          height: 200,
        },
        auth?.token
      );

      console.log(resCreateWidget);

      // Actualiza el estado con el nuevo widget
      setCards((prevCards) => [
        ...prevCards,
        {
          id: resCreateWidget.id,
          x: resCreateWidget.x,
          y: resCreateWidget.y,
          width: resCreateWidget.width,
          height: resCreateWidget.height,
          title: resCreateWidget.title,
          type: resCreateWidget.type,
        },
      ]);
    } catch (error) {
      console.error("Error creating widget:", error);
      // Maneja errores si es necesario
    }
    
    /* setCards(async (prevCards) => {
      const lastCard = prevCards[prevCards.length - 1];
      let newCardX = lastCard ? lastCard.x + 220 : 0;
      let newCardY = lastCard ? lastCard.y : 0;

      let spaceFound = false;
      while (!spaceFound) {
        spaceFound = true;
        for (let card of prevCards) {
          if (
            rectsOverlap(
              { x: newCardX, y: newCardY, width: 200, height: 200 },
              card
            )
          ) {
            spaceFound = false;
            newCardX += 220;
            if (newCardX + 200 > containerRef.current.offsetWidth) {
              newCardX = 0;
              newCardY += 220;
            }
            break;
          }
        }
      }

      try{
        console.log("createCard - WidgetProvider");
        const resCreateWitget = await createWidget(
          {
            type: type,
            title: title,
            description: "",
            configuration: config,
            dashboardId: dashboardId,
            x: newCardX,
            y: newCardY,
            width: 200,
            height: 200,
          },
          auth?.token
        );
        console.log(resCreateWitget);


        return [
          ...prevCards,
          {
            id: resCreateWitget.id,
            x: resCreateWitget.x,
            y: resCreateWitget.y,
            width: resCreateWitget.width,
            height: resCreateWitget.height,
            title: resCreateWitget.title,
            type: resCreateWitget.type,
          },
        ];
      }catch (error) {
        return [
          ...prevCards
        ]
      }
    }); */
  };

  const duplicateCard = (id) => {
    console.log("duplicate", id)
    setCards((prevCards) => {
      const pxCards = withPx(prevCards);
      const cardToDuplicate = pxCards.find((card) => card.id === id);
      if (!cardToDuplicate) return prevCards;

      let newCardX = cardToDuplicate.x + 220;
      let newCardY = cardToDuplicate.y;

      let spaceFound = false;
      while (!spaceFound) {
        spaceFound = true;
        for (let card of pxCards) {
          if (
            rectsOverlap(
              {
                x: newCardX,
                y: newCardY,
                width: cardToDuplicate.width,
                height: cardToDuplicate.height,
              },
              card
            )
          ) {
            spaceFound = false;
            newCardX += 220;
            if (
              newCardX + cardToDuplicate.width >
              containerRef.current.offsetWidth
            ) {
              newCardX = 0;
              newCardY += 220;
            }
            break;
          }
        }
      }

      return [
        ...prevCards,
        {
          id: prevCards.length + 1,
          x: newCardX,
          y: newCardY,
          width: cardToDuplicate.width,
          height: cardToDuplicate.height,
          layoutUnit: 'px',
          title: `Widget ${prevCards.length + 1}`,
          type: cardToDuplicate.type,
        },
      ];
    });
  };

  const deleteCard = async (id) => {
    console.log("deleteCard - WidgetProvider", id);
    try {
      await deleteWidget(id, auth?.token);
      setCards((prevCards) => prevCards.filter((card) => card.id !== id));
    } catch (error) {
      handleWidgetRequestError(error);
    }
  };

  const deleteAllCard = () => {
    setCards([]);
  };

  const updateCard = async (id, x, y, width, height) => {
    console.log("updateCard - WidgetProvider", id);
    // Se soltó el mouse: se congela el resultado final del preview
    // (drag/resize) y se persiste - el widget que se movió, MÁS cualquier
    // vecino que haya sido empujado para dejar de superponerse (antes esos
    // corrimientos quedaban solo visuales y se perdían al refrescar la
    // página). x/y/width/height llegan en pixeles efectivos; a partir de
    // acá cada card tocada queda guardada como % del contenedor actual.
    const draggedRect = { x: Math.max(x, 0), y: Math.max(y, 0), width, height };
    const { layout, changedIds } = resolveLayout(withPx(cards), id, draggedRect);
    changedIds.add(id);

    setDragPreview(null);

    const updates = layout
      .filter((c) => changedIds.has(c.id))
      .map((c) => ({
        id: c.id,
        percentValues: pxToPercent(
          { x: c.x, y: c.y, width: c.width, height: c.height },
          containerWidth
        ),
      }));

    setCards((prevCards) =>
      prevCards.map((card) => {
        const found = updates.find((u) => u.id === card.id);
        return found ? { ...card, ...found.percentValues } : card;
      })
    );

    try {
      await Promise.all(
        updates.map((u) => updateWidget(u.id, u.percentValues, auth?.token))
      );
    } catch (error) {
      handleWidgetRequestError(error);
    }
  };

  // Lo que consumen los componentes de UI (DashboardSpace, etc.) son
  // siempre pixeles efectivos ya resueltos para el ancho actual del
  // contenedor - así conviven sin fricción cards legadas en px (quietas) y
  // cards nuevas en % (responsivas). Mientras hay un drag/resize en curso,
  // se muestra la PREVISUALIZACIÓN del layout final (la card arrastrada en
  // su posición en vivo + los vecinos ya acomodados a su alrededor), sin
  // que nada de esto se haya confirmado todavía en `cards`.
  const displayCards = dragPreview
    ? resolveLayout(withPx(cards), dragPreview.id, dragPreview).layout
    : withPx(cards);

  return (
    <WidgetContext.Provider
      value={{
        loadingWidgets,
        containerRef,
        cards: displayCards,
        setCards,
        addCard,
        assignSpace,
        handleDrag,
        handleResize,
        duplicateCard,
        deleteCard,
        createCard,
        deleteAllCard,
        updateCard,
        refreshWidgetList
      }}
    >
      {children}
    </WidgetContext.Provider>
  );
};
